/* Voorwaarden: betalingstermijnen, aanvullende voorwaarden, de algemene voorwaarden
 * uit de bibliotheek (die gaan mee in de PDF), betaaltermijn en korting. */

import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { PaymentCondition, Quote } from '@/types/quote';
import { PaymentConditionsEditor } from '@/components/crm/quote/PaymentConditionsEditor';
import { StandardTermsSelector } from '@/components/crm/quote/StandardTermsSelector';
import { useQuoteTerms } from '@/hooks/useStandardTerms';

type Bewaar = (w: Partial<Quote>, o?: { silent?: boolean; refetch?: boolean }) => Promise<void>;
const stil = { silent: true, refetch: false } as const;

function Kop({ titel, uitleg }: { titel: string; uitleg?: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <h3 className="text-sm font-semibold">{titel}</h3>
      {uitleg && <span className="truncate text-xs text-muted-foreground">{uitleg}</span>}
    </div>
  );
}

export function VoorwaardenBlok({ quote, onUpdate }: { quote: Quote; onUpdate: Bewaar }) {
  const q = quote as Quote & { payment_term?: number | null; discount_percentage?: number | null; discount_amount?: number | null };
  const { selectedTermIds, setSelectedTerms } = useQuoteTerms(quote.id);
  const [aanvullend, setAanvullend] = useState(quote.additional_conditions ?? '');
  const wacht = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => setAanvullend(quote.additional_conditions ?? ''), [quote.id]);

  const getal = (veld: 'payment_term' | 'discount_percentage' | 'discount_amount', waarde: string, standaard: number) => {
    clearTimeout(wacht.current[veld]);
    wacht.current[veld] = setTimeout(() => onUpdate({ [veld]: waarde === '' ? standaard : Number(waarde) } as Partial<Quote>, stil), 500);
  };

  return (
    <div className="flex flex-col gap-4">
      <PaymentConditionsEditor
        conditions={quote.payment_conditions}
        onChange={(c: PaymentCondition[]) => onUpdate({ payment_conditions: c }, stil)}
      />

      <div className="flex flex-col divide-y rounded-xl border bg-background">
        <div className="flex flex-col gap-2 px-4 py-3">
          <Kop titel="Aanvullende voorwaarden" uitleg="Korte opmerkingen, bijvoorbeeld over btw." />
          <Textarea
            rows={2}
            value={aanvullend}
            placeholder="Bijvoorbeeld: de prijzen in deze offerte zijn exclusief btw."
            onChange={(e) => setAanvullend(e.target.value)}
            onBlur={() => aanvullend !== (quote.additional_conditions ?? '') && onUpdate({ additional_conditions: aanvullend || null }, stil)}
            className="min-h-[56px] text-sm"
          />
        </div>

        <div className="flex flex-col gap-2 px-4 py-3">
          <Kop titel="Algemene voorwaarden" uitleg="Uit de bibliotheek; gaan mee in de PDF." />
          <StandardTermsSelector selectedIds={selectedTermIds} onChange={(ids) => setSelectedTerms(ids)} />
          {selectedTermIds.length === 0 && (
            <p className="text-xs text-amber-700">Nog geen algemene voorwaarden gekozen.</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 px-4 py-3 sm:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payment_term" className="text-xs">Betaaltermijn (dagen)</Label>
            <Input id="payment_term" type="number" min={0} defaultValue={q.payment_term ?? 14} key={`pt-${quote.id}`}
              onChange={(e) => getal('payment_term', e.target.value, 14)} className="h-8" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="discount_percentage" className="text-xs">Korting (%)</Label>
            <Input id="discount_percentage" type="number" min={0} max={100} step={0.5} defaultValue={q.discount_percentage ?? 0} key={`dp-${quote.id}`}
              onChange={(e) => getal('discount_percentage', e.target.value, 0)} className="h-8" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="discount_amount" className="text-xs">Korting (€)</Label>
            <Input id="discount_amount" type="number" min={0} step={0.01} defaultValue={q.discount_amount ?? 0} key={`da-${quote.id}`}
              onChange={(e) => getal('discount_amount', e.target.value, 0)} className="h-8" />
          </div>
        </div>
      </div>
    </div>
  );
}
